/**
 * Review certificates - مشهد مراجعة (owner's requirement and clarification, 2026-11, item 3).
 *
 * The certificate is issued by the hospital or medical centre; the employee
 * uploads it here for HR review and documentation, optionally attaching it to
 * one of their leave requests (the approving manager and HR then see it with
 * the request).
 *
 *  - MyReviewCertificates: upload + the employee's own certificates and status (My Leave).
 *  - ReviewCertificatesPanel: HR's queue - view the file, accept or reject (Leave Admin).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { CheckCircle, Download, FileHeart, XCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LeaveAttachmentUploader } from '@/components/leave/LeaveAttachments';
import { downloadWithAuth } from '@/lib/downloadWithAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LINKABLE = ['pending_manager', 'pending_hr', 'approved'];

const STATUS = {
  pending: { cls: 'bg-amber-100 text-amber-800', en: 'Awaiting HR review', ar: 'بانتظار مراجعة الموارد البشرية' },
  accepted: { cls: 'bg-green-100 text-green-800', en: 'Accepted', ar: 'مقبول' },
  rejected: { cls: 'bg-red-100 text-red-800', en: 'Rejected', ar: 'مرفوض' },
};

const day = (value) => {
  try { return format(new Date(value), 'dd MMM yyyy'); } catch { return value; }
};

const StatusBadge = ({ status, isRTL }) => {
  const s = STATUS[status] || STATUS.pending;
  return <Badge className={s.cls}>{isRTL ? s.ar : s.en}</Badge>;
};

export const MyReviewCertificates = ({ isRTL, language, requests = [], onSubmitted }) => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [visitDate, setVisitDate] = useState('');
  const [facility, setFacility] = useState('');
  const [requestId, setRequestId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    axios.get(`${API_URL}/leave/review-certificates/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const linkable = requests.filter((r) => LINKABLE.includes(r.status) && !r.is_archived);
  const reset = () => { setFiles([]); setVisitDate(''); setFacility(''); setRequestId(''); setError(''); };

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(`${API_URL}/leave/review-certificates`, {
        document_id: files[0].id, visit_date: visitDate, facility_name: facility.trim(),
        leave_request_id: requestId || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setOpen(false); reset(); load(); onSubmitted?.();
    } catch (e) {
      setError(e.response?.data?.detail?.error || (isRTL ? 'تعذّر إرسال المشهد' : 'The certificate could not be sent'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="my-review-certificates">
      <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><FileHeart className="h-4 w-4" />{isRTL ? 'مشهد المراجعة' : 'Review certificates'}</CardTitle>
          <CardDescription>
            {isRTL ? 'ارفع مشهد المراجعة الصادر من المستشفى أو المركز الطبي لمراجعته من الموارد البشرية.'
              : 'Upload the certificate issued by the hospital or medical centre for HR review.'}
          </CardDescription>
        </div>
        <Button size="sm" variant="outline" onClick={() => { reset(); setOpen(true); }} data-testid="upload-review-certificate">
          {isRTL ? 'رفع مشهد مراجعة' : 'Upload certificate'}
        </Button>
      </CardHeader>
      {items.length > 0 && (
        <CardContent className="space-y-2">
          {items.map((x) => (
            <div key={x.id} className="flex flex-wrap items-center justify-between gap-2 border-b last:border-0 pb-2 text-sm" data-testid={`review-certificate-${x.id}`}>
              <span>{day(x.visit_date)} · {x.facility_name}</span>
              <span className="flex items-center gap-2">
                <StatusBadge status={x.status} isRTL={isRTL} />
                {x.review_note && <span className="text-xs text-slate-500">{x.review_note}</span>}
              </span>
            </div>
          ))}
        </CardContent>
      )}
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'رفع مشهد مراجعة' : 'Upload a review certificate'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'يظهر المشهد لك ولمديرك المباشر وللموارد البشرية فقط.' : 'Visible only to you, your direct manager and HR.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <LeaveAttachmentUploader token={token} value={files} onChange={(v) => setFiles(v.slice(-1))} isRTL={isRTL} required />
            <div className="space-y-1">
              <Label htmlFor="review-visit-date">{isRTL ? 'تاريخ المراجعة' : 'Visit date'} *</Label>
              <Input id="review-visit-date" type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} data-testid="review-visit-date" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="review-facility">{isRTL ? 'المستشفى / المركز الطبي' : 'Hospital / medical centre'} *</Label>
              <Input id="review-facility" value={facility} onChange={(e) => setFacility(e.target.value)} maxLength={200} data-testid="review-facility" />
            </div>
            {linkable.length > 0 && (
              <div className="space-y-1">
                <Label>{isRTL ? 'إرفاقه بطلب إجازة (اختياري)' : 'Attach to a leave request (optional)'}</Label>
                <Select value={requestId || 'none'} onValueChange={(v) => setRequestId(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="review-leave-request"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{isRTL ? 'بدون' : 'None'}</SelectItem>
                    {linkable.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {(language === 'ar' && r.leave_type_name_ar) || r.leave_type_name} · {day(r.start_date)} → {day(r.end_date)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {error && <p role="alert" className="text-sm text-red-700" data-testid="review-certificate-error">{error}</p>}
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
            <Button disabled={busy || !files.length || !visitDate || facility.trim().length < 2} onClick={submit} data-testid="submit-review-certificate">
              {isRTL ? 'إرسال' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export const ReviewCertificatesPanel = ({ isRTL }) => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [decide, setDecide] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    axios.get(`${API_URL}/leave/review-certificates?status=pending`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(`${API_URL}/leave/review-certificates/${decide.item.id}/review`,
        { action: decide.action, note: note.trim() || null }, { headers: { Authorization: `Bearer ${token}` } });
      setDecide(null); load();
    } catch (e) {
      setError(e.response?.data?.detail?.error || (isRTL ? 'تعذّر حفظ القرار' : 'The decision could not be saved'));
    } finally {
      setBusy(false);
    }
  };

  if (!items.length) return null;
  const rejecting = decide?.action === 'reject';
  return (
    <Card data-testid="review-certificates-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><FileHeart className="h-4 w-4" />{isRTL ? 'مشاهد المراجعة بانتظار المراجعة' : 'Review certificates awaiting review'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((x) => (
          <div key={x.id} className="flex flex-wrap items-center justify-between gap-2 border-b last:border-0 pb-2 text-sm" data-testid={`review-queue-${x.id}`}>
            <div className="min-w-0">
              <p className="font-medium">{(isRTL ? x.employee_name_ar : x.employee_name) || x.employee_name || x.employee_id}</p>
              <p className="text-slate-600">{day(x.visit_date)} · {x.facility_name}{x.leave_request_id ? (isRTL ? ' · مرفق بطلب إجازة' : ' · attached to a leave request') : ''}</p>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => downloadWithAuth(`${API_URL}/leave/attachments/${x.document_id}/download`, x.file_name || 'certificate', token)}>
                <Download className="h-4 w-4 me-1" />{isRTL ? 'عرض' : 'View'}
              </Button>
              <Button size="sm" variant="ghost" className="text-green-700" onClick={() => { setDecide({ item: x, action: 'accept' }); setNote(''); setError(''); }}>
                <CheckCircle className="h-4 w-4 me-1" />{isRTL ? 'قبول' : 'Accept'}
              </Button>
              <Button size="sm" variant="ghost" className="text-red-700" onClick={() => { setDecide({ item: x, action: 'reject' }); setNote(''); setError(''); }}>
                <XCircle className="h-4 w-4 me-1" />{isRTL ? 'رفض' : 'Reject'}
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
      <Dialog open={!!decide} onOpenChange={(v) => !busy && !v && setDecide(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{rejecting ? (isRTL ? 'رفض المشهد' : 'Reject the certificate') : (isRTL ? 'قبول المشهد' : 'Accept the certificate')}</DialogTitle>
          </DialogHeader>
          <Label htmlFor="review-note">
            {rejecting ? (isRTL ? 'السبب (10 أحرف على الأقل) *' : 'Reason (at least 10 characters) *') : (isRTL ? 'ملاحظة (اختياري)' : 'Note (optional)')}
          </Label>
          <Textarea id="review-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setDecide(null)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
            <Button variant={rejecting ? 'destructive' : 'default'} disabled={busy || (rejecting && note.trim().length < 10)} onClick={submit}>
              {rejecting ? (isRTL ? 'رفض' : 'Reject') : (isRTL ? 'قبول' : 'Accept')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
